import Grid from '@mui/material/Grid2';
import { useState } from 'react';
import { Box, Button, Typography, Link as MUILink } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import IntegrationPurchaseDrawer from './IntegrationPurchaseDrawer';
import { useGetCatalogAvailableTools, usePurchaseCatalogIntegration } from 'apis/agency/integrations/hook';
import { useToast } from '@/hooks/toast';
import MarketPlaceEmptyStateView from './MarketPlaceEmptyStateView';
import IntegrationLoader from './Loader';

export default function MarketPlace({
  integrations,
  installedIntegrations,
  onPurchaseSuccess,
  isLoading
}: {
  integrations: any;
  installedIntegrations: any;
  isLoading?: boolean;
  onPurchaseSuccess: () => void | Promise<void>;
}) {
  const [selectedIntegration, setSelectedIntegration] = useState<any | null>(null);
  const [isPaying, setIsPaying] = useState(false);
  const { data: integrationTools = [], isLoading: toolsLoading } = useGetCatalogAvailableTools(
    selectedIntegration?.code
  );
  const { mutateAsync: purchaseCatalogIntegration } = usePurchaseCatalogIntegration({
    onSuccess: async () => {
      showToast(`${selectedIntegration?.name} has been activated successfully.`, 'success');
      closeDrawer();
      await onPurchaseSuccess();
    }
  });
  const { ToastComponent, showToast } = useToast();

  const closeDrawer = () => setSelectedIntegration(null);

  const handlePurchaseIntegration = async () => {
    if (!selectedIntegration) return;

    try {
      setIsPaying(true);
      await purchaseCatalogIntegration(selectedIntegration.code);
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Unable to complete payment. Please try again.', 'error');
    } finally {
      setIsPaying(false);
    }
  };

  const installedCodes = installedIntegrations?.data?.map((i: any) => i.code) || [];

  if (isLoading) {
    return <IntegrationLoader />;
  }
  return (
    <>
      <Grid container spacing={2} mt={1}>
        {integrations?.data?.length ? (
          integrations?.data?.map((item: any, index: any) => {
            const isInstalled = installedCodes.includes(item.code);
            const hasOneTimeFee = Number(item.one_time_fee || 0) > 0;

            return (
              <Grid
                key={index}
                size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 3 }}
                bgcolor='white'
                p={2}
                border='1px solid #ECECEC'
                borderRadius={2}
                display='flex'
                flexDirection='column'
                justifyContent='space-between'
              >
                <Box>
                  <img src={item.avatar_url} alt={item.name} style={{ maxHeight: 30 }} />
                  <Typography my={1} color='secondary' fontSize={14}>
                    <ReactMarkdown
                      components={{
                        a: ({ ...props }) => (
                          <MUILink
                            {...(props as any)}
                            component='a'
                            target='_blank'
                            rel='noopener'
                            sx={{ color: 'primary.main', fontWeight: 500 }}
                          />
                        ),
                        p: ({ ...props }) => <span {...props} />
                      }}
                    >
                      {item?.description}
                    </ReactMarkdown>
                  </Typography>
                </Box>

                <Box display='flex' justifyContent='center' mt={2}>
                  {isInstalled ? (
                    <Button
                      fullWidth
                      variant='outlined'
                      disabled
                      sx={{
                        borderRadius: 20,
                        px: 4,
                        textTransform: 'none',
                        fontWeight: 600,
                        color: '#777777',
                        borderColor: '#FAFAFA',
                        bgcolor: '#FAFAFA'
                      }}
                    >
                      Installed
                    </Button>
                  ) : (
                    <>
                      {hasOneTimeFee ? (
                        <Button
                          fullWidth
                          variant='outlined'
                          sx={{
                            borderRadius: 20,
                            px: 4,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderColor: 'primary.main'
                          }}
                          onClick={() => setSelectedIntegration(item)}
                        >
                          Get Voice Ready Integration
                        </Button>
                      ) : (
                        <Button
                          fullWidth
                          variant='outlined'
                          sx={{
                            borderRadius: 20,
                            px: 4,
                            textTransform: 'none',
                            fontWeight: 600
                          }}
                          onClick={() =>
                            window.open('https://support.kickcall.ai/?b=68f0ab40a77f2a9129036527', '_blank')
                          }
                        >
                          Request
                        </Button>
                      )}
                    </>
                  )}
                </Box>
              </Grid>
            );
          })
        ) : (
          <MarketPlaceEmptyStateView />
        )}
      </Grid>

      <IntegrationPurchaseDrawer
        integration={selectedIntegration}
        tools={integrationTools}
        toolsLoading={toolsLoading}
        isPaying={isPaying}
        onClose={closeDrawer}
        onPay={handlePurchaseIntegration}
      />
      {ToastComponent}
    </>
  );
}
